import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface PackageParticipantDetail {
    package_id: number;
    package_name: string;
    total: number;
    paid: string;
    unpaid: string;
}

export interface InstitutionParticipantDetail {
    institution: string;
    total: number;
    paid: string;
    unpaid: string;
}

export interface ParticipantRegistrationItem {
    registration_id: number;
    registration_number: string;
    registration_date: string;
    current_step?: number;
    notes?: string;
    participant_id: number;
    pic_id?: number | null;
    full_name: string;
    email: string;
    phone_mobile?: string | null;
    phone_number?: string | null;
    gender?: string | null;
    institution?: string | null;
    position?: string | null;
    registration_type?: string | null;
    pic?: {
        participant_id: number;
        full_name: string;
        email?: string | null;
        phone_mobile?: string | null;
        institution?: string | null;
        position?: string | null;
    } | null;
    package_id: number;
    package_name?: string | null;
    package_price?: number | null;
    payment_status?: string | null;
    invoice_number?: string | null;
    latest_payment?: {
        payment_id?: number;
        status?: string;
        invoice_number?: string;
        customer_number?: string;
        amount?: string | number;
        [key: string]: any;
    } | null;
    package?: {
        package_id: number;
        name: string;
        price: string | number;
        code?: string;
        [key: string]: any;
    } | null;
    participant?: {
        participant_id: number;
        full_name: string;
        email: string;
        institution?: string | null;
        phone_mobile?: string | null;
        registration_type?: string | null;
        [key: string]: any;
    } | null;
}

export interface PeriodSummary {
    status: string;
    total_participants: number;
    total_packages: number;
    total_biaya_keseluruhan: number;
    total_biaya_paid: number;
    total_biaya_unpaid: number;
    paid: number;
    unpaid: number;
    participants_per_package: PackageParticipantDetail[];
    participants_per_institution: InstitutionParticipantDetail[];
    total_institutions: number;
    fully_paid_institutions: number;
    not_fully_paid_institutions: number;
    summary?: {
        total_participants: number;
        total_packages: number;
        total_biaya_keseluruhan: number;
        total_biaya_paid: number;
        total_biaya_unpaid: number;
        paid: number;
        unpaid: number;
        participants_per_package: PackageParticipantDetail[];
        participants_per_institution: InstitutionParticipantDetail[];
        total_institutions: number;
        fully_paid_institutions: number;
        not_fully_paid_institutions: number;
    };
    data?: ParticipantRegistrationItem[];
    participants?: ParticipantRegistrationItem[];
    meta?: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export interface PeriodSummaryParams {
    search?: string;
    package_id?: string | number;
    payment_status?: string;
    status?: string;
    registration_type?: string;
    institution?: string;
    gender?: string;
    blood_type?: string;
    clothing_size?: string;
    payment_date_from?: string;
    payment_date_to?: string;
    page?: number;
    per_page?: number;
}

export const usePeriodSummary = (periodId: string, params?: PeriodSummaryParams) => {
    const cleanParams: Record<string, any> = {};
    if (params?.search) cleanParams.search = params.search;
    if (params?.package_id && params.package_id !== "all") cleanParams.package_id = params.package_id;
    if (params?.payment_status && params.payment_status !== "all") cleanParams.payment_status = params.payment_status;
    if (params?.status && params.status !== "all") cleanParams.status = params.status;
    if (params?.registration_type && params.registration_type !== "all") cleanParams.registration_type = params.registration_type;
    if (params?.institution && params.institution !== "all") cleanParams.institution = params.institution;
    if (params?.gender && params.gender !== "all") cleanParams.gender = params.gender;
    if (params?.blood_type && params.blood_type !== "all") cleanParams.blood_type = params.blood_type;
    if (params?.clothing_size && params.clothing_size !== "all") cleanParams.clothing_size = params.clothing_size;
    if (params?.payment_date_from) cleanParams.payment_date_from = params.payment_date_from;
    if (params?.payment_date_to) cleanParams.payment_date_to = params.payment_date_to;
    if (params?.page) cleanParams.page = params.page;
    if (params?.per_page) cleanParams.per_page = params.per_page;

    return useQuery<PeriodSummary>({
        queryKey: ["period-summary", periodId, cleanParams],
        queryFn: async () => {
            const { data } = await axiosClient.get(
                `/periods/${periodId}/summary`,
                {
                    params: cleanParams,
                    withCredentials: true,
                }
            );
            return data;
        },
        enabled: !!periodId,
        placeholderData: keepPreviousData,
    });
};
